<template>
    <div class="product-detail">
        <div class="product-detail__top" :style="topBackgroundStyle">
            <div class="product-detail__name" ref="productName">
                {{ lastScanData?.product?.name
                }}<template v-if="lastScanData?.product?.year"
                    >, {{ lastScanData.product.year }}</template
                >
            </div>
            <div class="product-detail__bottle-wrap">
                <div class="product-detail__circulation">
                    <div>Item</div>
                    <div class="product-detail__circulation-bold">
                        №{{ lastScanData?.label_number ?? '—' }}
                    </div>
                    <div>out of {{ lastScanData?.product_total ?? '—' }}</div>
                </div>
                <img
                    :src="productImageUrl || productPlaceholder"
                    alt=""
                    class="product-detail__bottle"
                />
            </div>
        </div>
        <div
            ref="productInfo"
            class="product-detail__info"
            :class="{ 'product-detail__info--scroll-locked': !isInfoScrolledToTop }"
            :style="{
                top: productInfoStickyTop,
                'max-height': productInfoMaxHeight,
            }"
        >
            <div
                ref="productInfoContent"
                class="product-detail__info-content"
                :style="{
                    'max-height': productInfoMaxHeight,
                }"
            >
                <div class="product-detail__text-wrap">
                    <div class="product-detail__desc" v-html="safeDescription"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import productPlaceholder from '@/assets/images/product_placeholder.png'
import { useViewport } from '@/composables/useViewport'
import { normalizeLastScannedProduct } from '@/services/api'
import { useUiStore } from '@/stores/ui'
import { LastScannedProduct } from '@/types'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const LAST_SCAN_KEY = 'last_scan'

// Viewport
useViewport()

// State
const lastScanData = ref<LastScannedProduct | null>(null)
const isLoading = ref(false)
const error = ref<string | null>(null)
const productName = ref<HTMLDivElement | null>(null)
const productInfoContent = ref<HTMLDivElement | null>(null)
const productNameHeight = ref(0)
const isInfoScrolledToTop = ref(false)
let nameResizeObserver: ResizeObserver | null = null
let windowResizeHandler: (() => void) | null = null

const productImageUrl = computed(() => lastScanData.value?.product?.product_image?.url)
const topBackgroundStyle = computed(() =>
    lastScanData.value?.product?.bg_image
        ? {
              backgroundImage: `url(${lastScanData.value.product.bg_image.url})`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
          }
        : {}
)
const productInfoStickyTop = computed(() => `${productNameHeight.value}px`)
const productInfoMaxHeight = computed(
    () => `calc(var(--vh, 1vh) * 100 - var(--app-footer-height) - ${productNameHeight.value}px)`
)

// Computed properties
// const productDishes = computed(() => scanData.value?.product?.dishes || [])

const defaultDescription = 'Описание будет добавлено позже.'

// Simple HTML sanitization for description
const sanitizeDescription = (html: string | undefined | null): string => {
    if (!html) return defaultDescription

    // Remove potentially dangerous tags and attributes
    return html
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/on\w+="[^"]*"/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
        .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
        .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
}

const safeDescription = computed(() =>
    sanitizeDescription(lastScanData.value?.product?.description)
)

const handleInfoScroll = () => {
    if (!productInfoContent.value || !productName.value) return

    // Обновляем viewport перед проверкой для корректной работы в мобильных браузерах
    // Это важно, так как viewport может изменяться при скролле из-за адресной строки
    const updateViewport = () => {
        const height = window.visualViewport?.height || window.innerHeight
        const vh = height * 0.01
        document.documentElement.style.setProperty('--vh', `${vh}px`)
    }
    updateViewport()

    const infoRect = productInfoContent.value.getBoundingClientRect()
    const nameRect = productName.value.getBoundingClientRect()

    // Скролл разблокирован только когда доскроллили до нижней границы productName
    // Добавляем небольшой threshold (2px) для более надежной работы на мобильных браузерах
    const threshold = 120
    const shouldUnlock = infoRect.top <= nameRect.bottom + threshold
    isInfoScrolledToTop.value = shouldUnlock
}

// Lifecycle
onMounted(async () => {
    try {
        isLoading.value = true
        error.value = null
        const ui = useUiStore()
        ui.beginDataLoad()
        const stored = localStorage.getItem(LAST_SCAN_KEY)
        if (stored) {
            lastScanData.value = normalizeLastScannedProduct(
                JSON.parse(stored) as LastScannedProduct
            )
        } else {
            error.value = 'No scan data found'
        }
    } catch (e: unknown) {
        error.value = (e as Error)?.message || 'Ошибка загрузки продукта'
    } finally {
        isLoading.value = false
        const ui = useUiStore()
        ui.endDataLoad()
    }

    const updateNameHeight = () => {
        productNameHeight.value = Math.ceil(productName.value?.getBoundingClientRect().height || 0)
        requestAnimationFrame(handleInfoScroll)
    }

    updateNameHeight()

    if ('ResizeObserver' in window && productName.value) {
        nameResizeObserver = new ResizeObserver(() => {
            // Добавляем debounce для улучшения производительности
            requestAnimationFrame(updateNameHeight)
        })
        nameResizeObserver.observe(productName.value)
    } else {
        // Fallback: обновление по ресайзу окна с passive listener
        windowResizeHandler = () => requestAnimationFrame(updateNameHeight)
        window.addEventListener('resize', windowResizeHandler, { passive: true })
    }

    // Add scroll listeners
    if (productInfoContent.value) {
        productInfoContent.value.addEventListener('scroll', handleInfoScroll, { passive: true })
    }
    window.addEventListener('scroll', handleInfoScroll, { passive: true })

    // Проверяем начальное состояние
    handleInfoScroll()
})

onBeforeUnmount(() => {
    if (nameResizeObserver) {
        nameResizeObserver.disconnect()
        nameResizeObserver = null
    }
    if (windowResizeHandler) {
        window.removeEventListener('resize', windowResizeHandler)
        windowResizeHandler = null
    }
    if (productInfoContent.value) {
        productInfoContent.value.removeEventListener('scroll', handleInfoScroll)
    }
    window.removeEventListener('scroll', handleInfoScroll)
})
</script>

<style scoped lang="stylus">
.taste
    display flex
    align-items stretch
    gap 16px
    padding 10px 12px
    background-color grey
    border-radius 16px
    &_dark
        background-color transparent
        padding 8px 0 0
    &__border
        width 1px
        // height 100%
        background-color white
        ^[0]_dark &
            opacity 0.2
            background-color var(--app-text-color)
    &__title
        text-align center
        font-size 16px
        line-height 19px
        font-weight 600
        color var(--app-text-light-color)
        ^[0]_dark &
            text-align left
            color var(--app-text-color)
    &__food-icon-wrap
        display flex
        gap 8px
    &__food-icon
        width 32px
        height 32px
        ^[0]_dark &
            width 37px
            height 37px
            background var(--app-black)
            border-radius 50%
        & img
            width 100%
            height 100%
            object-fit contain
    &__item
        display flex
        flex-direction column
        gap 12px
    &__temp
        background-color var(--app-bg-color)
        padding 9px 12px 7px
        border-radius 7px
        color var(--app-text-color)
        font-size 14px
        line-height 17px
        font-weight 500
        ^[0]_dark &
            padding 12px 12px 10px
            border-radius 8px
            background-color var(--app-black)
            color var(--app-text-light-color)
            font-size 13px
            line-height 13px

.product-detail
    &__btn-fixed-wrap
        position fixed
        z-index 20
        left 0
        bottom var(--app-footer-height)
        width 100%
        padding 8px 16px 20px
        background transparent
        transition background-color 0.3s ease

        &--highlighted
            background var(--app-bg-color)

    &__btn-fixed
        width 100%
    // &__text-wrap
    //     display flex
    //     flex-direction column
    //     gap 12px
    //     padding-bottom 24px
    &__title
        font-size 20px
        line-height 26px
        font-weight 600
        color var(--app-text-color)
    &__desc
        padding-bottom var(--app-footer-height)
        font-size 16px
        line-height 24px
        font-weight 400
        color var(--app-text-color)
    &__actions
        margin-top 32px
        padding 0 0 84px
        z-index 10
        display flex
        flex-direction column
        gap 12px
        background var(--app-bg-color)
    &__info
        z-index 10
        position sticky
        background var(--app-bg-color)
        border-radius 16px 16px 0 0
        margin-top -45px
        padding 0

        &--scroll-locked
            overflow hidden
        &:before
            content ''
            position absolute
            left 0
            top 0
            width 100%
            height 20px
            background var(--app-bg-color)
            border-radius 16px 16px 0 0
            z-index 10

    &__info-content
        width 100%
        height 100%
        padding 32px 16px 0
        overflow-y auto

        .product-detail__info--scroll-locked &
            overflow hidden
    &__top
        position sticky
        top 0
        z-index 1
        background rgba(202 210 43, .7)
        // height calc(var(--vh, 1vh) * 100 - var(--app-footer-height))
        padding-bottom 15%
    &__winemaker-name
        font-weight 600
    &__name
        max-width 360px
        margin 0 auto
        padding 24px 16px 16px
        font-size 28px
        line-height 33px
        font-weight 400
        color var(--app-text-light-color)
        text-align center
    &__bottle
        width 100%
        // height 100%
        // margin-top 25%
        object-fit contain
        &-wrap
            position relative
            width 100%
            margin-top 20%
            text-align center
    &__circulation
        position absolute
        right 0px
        flex-shrink 0
        width 100px
        height 100px
        display flex
        flex-direction column
        justify-content center
        align-items center
        gap 3px
        // margin-top 10%
        margin-right 8%
        background-color var(--app-black)
        border-radius 50%
        padding 10px 5px 5px
        font-size 13px
        line-height 16px
        font-weight 400
        color var(--app-text-light-color)
        text-align center
        &-bold
            font-weight 600
            font-size 18px
            line-height 18px
    &__text-wrap
        display flex
        flex-direction column
        gap 16px
        padding 20px 0 0
    &__title
        font-size 20px
        line-height 26px
        font-weight 600
        color var(--app-text-color)
    &__desc
        font-size 16px
        line-height 22px
        font-weight 400
        color var(--app-text-color-secondary)
</style>

<!-- Global styles for HTML content in product description -->
<style lang="stylus">
.product-detail__desc
    // Styles for HTML content in description
    img
        display block
        width 100%
        height auto
        margin 12px 0
        border-radius 12px
    p, div
        margin 0 0 24px 0
        font-size 16px
        line-height 22px
        font-weight 400
        color var(--app-text-color-secondary)
        &:last-child
            margin-bottom 0
    h1, h2, h3, h4, h5, h6
        margin 0 0 12px 0
        font-weight 600
        color var(--app-text-color)
    h1
        font-size 22px
        line-height 28px
    h2
        font-size 20px
        line-height 26px
    h3
        font-size 18px
        line-height 24px
    ul, ol
        margin 12px 0
        padding-left 24px
    li
        margin 6px 0
    strong, b
        font-weight 600
    em, i
        font-style italic
    a
        color var(--app-main-accent-color)
        text-decoration none
        &:hover
            text-decoration underline
    blockquote
        margin 16px 0
        padding 12px 16px
        border-left 4px solid var(--app-main-accent-color)
        background rgba(0, 0, 0, 0.05)
        border-radius 4px
        font-style italic
</style>
